import ButtonPlayerView from "shared/components/buttons/ButtonPlayerView.js";
import ItemRowPlayerAssist from "shared/components/items/ItemRowPlayerAssist.js";
import LoadingSquare from "shared/components/loading/LoadingSquare";
import React, { useEffect, useState } from "react";
import { useOutletContext, useSearchParams } from "react-router-dom";
import { getPlayers, getPlayerPricings, getPlayerYearValuations } from "shared/services/api-assistant.js";
import FilterContainerPlayer from "shared/components/filters/FilterContainerPlayer.js";
import PopupInformationPricing from "shared/components/popups/PopupInformationPricing.js";
import BoxCard from "shared/components/box/BoxCard.js";
import { getPlayerPricing } from "shared/utils/player.js";
import Count from "shared/components/counts/Count.js";
import ButtonSelect from "shared/components/buttons/ButtonSelect.js";

const PageUserPlayers = () => {
  const user = useOutletContext();
  const [searchParams, setSearchParams] = useSearchParams();

  const [defaultFilters] = useState({
    activePlayersOnly: true,
    firstPositionOnly: false,
    positions: undefined,
    minAge: undefined,
    maxAge: undefined,
    minOvr: undefined,
    maxOvr: undefined,
  });
  const [filters, setFilters] = useState(defaultFilters);

  const [players, setPlayers] = useState(null);
  const [pricings, setPricings] = useState([]);
  const [yearValuations, setYearValuations] = useState([]);

  const [playerView, setPlayerView] = useState(null);
  const playerMode = searchParams.get("view") === "arena" ? "arena" : "pro";
  const setPlayerMode = (view) => {
    const next = new URLSearchParams(searchParams);
    next.set("view", view);
    setSearchParams(next);
  };

  const fetchPlayers = () => {
    setPlayers(null);

    getPlayers({
      handleSuccess: (d) => {
        setPlayers(d.data.getPlayers);
      },
      handleError: (e) => console.log(e),
      params: {
        ...filters,
        arenaOnly: playerMode === "arena",
        owners: [user.id],
        limit: 50000,
      },
    });
  };

  const fetchPricings = () => {
    getPlayerPricings({
      handleSuccess: (d) => {
        if (d.data.getPlayerPricings) {
          setPricings(d.data.getPlayerPricings);
        }
      },
      handleError: (e) => console.log(e),
    });
  };

  const fetchPlayerYearValuations = () => {
    getPlayerYearValuations({
      handleSuccess: (d) => {
        if (d.data.getPlayerYearValuations) {
          setYearValuations(d.data.getPlayerYearValuations);
        }
      },
      handleError: (e) => console.log(e),
    });
  };

  const countFilters = (p) => {
    return Object.keys(filters).reduce((count, key) => {
      if (
        key !== "search" &&
        filters[key] != null &&
        filters[key] != false &&
        filters[key] !== "" &&
        (!Array.isArray(filters[key]) || filters[key].length > 0)
      ) {
        if (key !== "activePlayersOnly") {
          return count + 1;
        }
      }
      return count;
    }, 0);
  };

  const calculateTotalPricing = (players) => {
    let total = 0;
    let allValid = true;

    for (const player of players) {
      const price = getPlayerPricing(player, pricings, yearValuations);

      const isValid = price !== null && price !== undefined && !Number.isNaN(price);

      if (!isValid) {
        allValid = false;
        continue;
      }

      total += Number(price);
    }

    return {
      total: Math.round(total * 10) / 10,
      complete: allValid,
    };
  };

  useEffect(() => {
    fetchPricings();
    fetchPlayerYearValuations();
  }, []);

  useEffect(() => {
    const requestedView = searchParams.get("view");
    if (requestedView !== "pro" && requestedView !== "arena") {
      const next = new URLSearchParams(searchParams);
      next.set("view", "pro");
      setSearchParams(next, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  useEffect(() => {
    if (user && filters === defaultFilters) {
      fetchPlayers();
    }
  }, [filters]);

  useEffect(() => {
    if (user) fetchPlayers();
  }, [playerMode, user]);

  return (
    <div id="PageUserPlayers">
      <div className="container-xl px-1 px-md-4 py-4">
        <div className="d-flex flex-column">
          <div className="d-flex flex-fill flex-column flex-md-row flex-column-reverse">
            <BoxCard
              title={
                <div style={{ minWidth: "100px" }}>
                  {players !== null ? (
                    <Count label="Players" count={players.length} />
                  ) : (
                    <LoadingSquare height={"50px"} />
                  )}
                </div>
              }
            ></BoxCard>
            <BoxCard
              title={
                <div style={{ minWidth: "200px" }}>
                  {playerMode === "arena" ? (
                    <Count label="Gallery value estimation" count="$--" />
                  ) : players && pricings ? (
                    <Count
                      label={
                        <div className="d-flex flex-row">
                          Gallery value estimation &nbsp;
                          <PopupInformationPricing />
                        </div>
                      }
                      count={"$" + calculateTotalPricing(players).total}
                    />
                  ) : (
                    <LoadingSquare className="w-100" height={"50px"} />
                  )}
                </div>
              }
            ></BoxCard>
            <div className="d-flex flex-column flex-fill flex-sm-row justify-content-end align-items-center">
              <div>
                {playerMode === "pro" && pricings && players && yearValuations && !calculateTotalPricing(players).complete && (
                  <div className="mb-3 text-warning mx-1">
                    <i className="bi bi-exclamation-circle-fill me-1" />
                    Pricing is missing for some players
                  </div>
                )}
              </div>
              <div className="d-flex flex-fill flex-row justify-content-end mx-1">
                <ButtonSelect
                  ariaLabel="Player type"
                  className="d-flex border me-1"
                  buttonClassName="btn"
                  buttons={{ pro: "Pro", arena: "Arena" }}
                  selected={playerMode}
                  onChange={setPlayerMode}
                />
                {countFilters() > 0 && (
                  <button
                    className="btn btn-warning text-white me-1"
                    onClick={() => {
                      setFilters(defaultFilters);
                    }}
                  >
                    <i className="bi bi-x-square-fill text-white"></i>
                  </button>
                )}
                <FilterContainerPlayer
                  trigger={
                    <button className="d-flex flex-row btn btn-info text-white me-1">
                      <i className="bi bi-filter-square-fill" />
                      {countFilters() > 0 ? <div className="ms-2">{countFilters()}</div> : ""}
                    </button>
                  }
                  filters={filters}
                  onChange={(f) => setFilters(f)}
                  onApply={() => fetchPlayers()}
                  showContractSection={true}
                  showRetirementSection={true}
                  showPositions={true}
                  showOverallScore={true}
                  showAge={true}
                  deactivateNavigate={true}
                />
              </div>
            </div>
          </div>

          <div className="d-flex flex-fill flex-column flex-md-row"></div>
          <BoxCard
            title={<div>Player list</div>}
            actions={
              user &&
              players !== null && (
                <div className="d-flex justify-content-end">
                  <ButtonPlayerView
                    selectedView={playerView}
                    onChange={(v) => setPlayerView(v)}
                    displayPricing={true}
                    displayTraits={true}
                  />
                </div>
              )
            }
            content={
              <div className="d-flex flex-fill flex-column mt-2">
                {user && players !== null && (
                  <div>
                    {players.map((c) => (
                      <ItemRowPlayerAssist
                        p={c}
                        display={playerView}
                        pricing={getPlayerPricing(c, pricings, yearValuations)}
                      />
                    ))}
                  </div>
                )}

                {(!user || players === null) && (
                  <div style={{ height: 300 }}>
                    <LoadingSquare height={300} />
                  </div>
                )}
              </div>
            }
          />
        </div>
      </div>
    </div>
  );
};

export default PageUserPlayers;
